import { call } from '@advanza/api'
import { InputField, SelectSimple } from '@advanza/input'
import { WhatsappSvg } from '@advanza/misc/WhatsappSvg'
import { Modal } from '@advanza/modal'
import { Button, Divider, Ellipsis, Icon, LoadingDots, PreIcon } from '@advanza/ui'
import NiceModal from '@ebay/nice-modal-react'
import {
    SUBSCRIPTION_STATUS_ACTIVE,
    SUBSCRIPTION_STATUS_CANCELLED,
    SUBSCRIPTION_STATUS_UNVERIFIED,
} from '@trustoo/types/entity/paymentInfo'
import ConfirmSendWhatsAppMessageModal from 'components/billing/ConfirmSendWhatsAppMessageModal'
import { STATUS_META } from 'components/billing/debtorNew/ui'
import InvoiceItem from 'components/billing/InvoiceItem'
import InvoiceStatusHistory, { statusMap } from 'components/billing/InvoiceStatusHistory'
import MolliePaymentLink from 'components/billing/MolliePaymentLink'
import MonthPicker from 'components/billing/MonthPicker'
import UnpaidInvoiceStatus from 'components/billing/UnpaidInvoiceStatus'
import Card from 'components/Card'
import CheckButton from 'components/CheckButton'
import AutoRow from 'components/misc/AutoRow'
import Pagination from 'components/Pagination'
import ServicesSelect from 'components/services/ServicesSelect'
import TableList from 'components/TableList'
import A from 'components/ui/A'
import Label from 'components/ui/Label'
import LoadBoxWrapper from 'components/ui/LoadBoxWrapper'
import { format, sub } from 'date-fns'
import { useInvoiceActions, useInvoicesList } from 'hooks/billingHooks'
import { useAppSelector } from 'hooks/hooks'
import { useDebouncedAmountFilter, useUrlQuery } from 'hooks/miscHooks'
import queryString from 'query-string'
import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import BatchMailButton from './BatchMailButton'

const definitions = {
    invoice_id: { name: '#' },
    subscriptionStatus: { name: 'sub status' },
    businessName: { name: 'name' },
    amount_bruto: { name: '€' },
    amount_netto: { name: '€netto' },
    open_amount: { name: 'open amount' },
    open_amount_value: { name: '€Open' },
    invoice_nr: { name: 'invoice nr' },
    transaction_status: { name: 'status' },
    datetime: { name: 'created' },
    invoice_about_month: { name: 'about month' },
    pay_method: { name: 'pay method' },
    description: { name: 'description' },
    pay_datetime: { name: 'pay' },
    invoice_sent_date: { name: 'mail sent' },
    reminder_sent_date: { name: 'reminder sent' },
    second_reminder_sent_date: { name: 'second reminder sent' },
    final_reminder_sent_date: { name: 'final reminder sent' },
    unpaidStatus: {
        name: <Icon name="monetization_on" fontSize={18} title="unpaid unvoice follow up" />,
    },
    downloadInvoice: { name: <Icon name="get_app" /> },
    processed_batch: { name: <Icon name="import_export" /> },
}

const definitionsBrief = {
    invoice_id: { name: '#' },
    subscriptionStatus: { name: 's' },
    businessName: { name: 'name' },
    amount_netto: { name: '€netto' },
    open_amount: { name: 'open' },
    open_amount_value: { name: '€Open' },
    description: { name: 'description' },
    invoice_nr: { name: 'invoice nr' },
    transaction_status: { name: 'status' },
    datetime: { name: 'created' },
    unpaidStatus: {
        name: <Icon name="monetization_on" fontSize={18} title="unpaid unvoice follow up" />,
    },
    invoice_about_month: { name: 'about month' },
    downloadInvoice: { name: <Icon name="get_app" /> },
    mollie_payment_link: { name: <Icon name="link" /> },
    sendWhatsapp: {
        name: (
            <div
                title={
                    'send a whatsapp message to the provider, if the invoice is not paid||fulfilled||succeeded'
                }>
                <WhatsappSvg />
            </div>
        ),
    },
}

const subStatusMap = {
    [SUBSCRIPTION_STATUS_ACTIVE]: { icon: 'play_arrow', color: '#0ea66b' },
    [SUBSCRIPTION_STATUS_CANCELLED]: { icon: 'delete_forever', color: '#ff002f' },
    suspended: { icon: 'delete_forever', color: '#ff002f' },
    [SUBSCRIPTION_STATUS_UNVERIFIED]: { icon: 'pending', color: '#ff8000' },
}

// Mirrors `InvoicesTable::SUCCESS_STATUSES` on the backend.
const SUCCESS_STATUSES = ['fulfilled', 'paid', 'succeeded']

// Falls back to the full invoice amount when `open_amount` is missing or unparsable.
const getInvoiceAmounts = (invoice) => {
    const total = Number(invoice.amount_netto) || 0
    const parsedOpen = Number(invoice.open_amount)
    return { total, open: Number.isFinite(parsedOpen) ? parsedOpen : total }
}

function renderRow(id, entities, expanded) {
    const { invoices } = entities
    const invoice = invoices[id]
    const subStatus =
        subStatusMap[invoice.subscriptionStatus] || subStatusMap[SUBSCRIPTION_STATUS_UNVERIFIED]
    const { protocol, hostname } = window.location
    const rowBackgroundColor = (() => {
        if (invoice._isLoading) {
            return '#b0b0b0'
        }
        if (invoice._actionStatus === 'success') {
            return '#bbfaa1'
        }
        if (invoice._actionStatus === 'error') {
            return '#faa1ab'
        }
        return null
    })()

    return {
        cols: Object.keys(expanded ? definitions : definitionsBrief).map((key) => {
            switch (key) {
                case 'datetime':
                case 'pay_datetime':
                    return invoice[key] && format(new Date(invoice[key]), 'd MMM')
                case 'invoice_about_month':
                    return format(new Date(invoice[key]), 'MMM yy')
                case 'amount_bruto':
                case 'amount_netto':
                    return '€' + invoice[key].toFixed(2)
                case 'open_amount': {
                    const { total, open } = getInvoiceAmounts(invoice)
                    if (open <= 0) {
                        return <Label text="Fully Paid" bgColor="#0ea66b" color="#fff" />
                    }
                    if (total <= 0 || open >= total) {
                        return <Label text="Open" bgColor="#ff9800" color="#fff" />
                    }
                    return <Label text="Partially Paid" bgColor="#42a5f5" color="#fff" />
                }
                case 'open_amount_value': {
                    const { total, open } = getInvoiceAmounts(invoice)
                    if (open <= 0 || total <= 0 || open >= total) {
                        return null
                    }
                    return '€' + open.toFixed(2)
                }
                case 'subscriptionStatus':
                    return (
                        <span title={invoice[key]}>
                            <Icon name={subStatus.icon} style={{ color: subStatus.color }} />
                        </span>
                    )
                case 'processed_batch':
                    return (
                        <Icon title="processed bach" name="done_outline" primColor={invoice[key]} />
                    )
                case 'unpaidStatus':
                    return (
                        <UnpaidInvoiceStatus
                            unpaidInvoiceFollowUp={invoice.unpaid_invoice_follow_up}
                            paytUrl={invoice.payt_url}
                            paytPausedAt={invoice.payt_paused_at}
                            paytPausedByUser={invoice.payt_paused_by_user}
                        />
                    )
                case 'transaction_status': {
                    // Once the invoice is in a success status (paid/fulfilled), prefer that over
                    // a stale `debt_collection` timestamp — the case has been resolved. Surface the
                    // historical fact via a small gavel icon next to the badge so the row is still
                    // distinguishable from a regular paid invoice. The `debt_collection` timestamp
                    // is preserved in the DB so finance filters/exports keep working.
                    const isPaidStatus = SUCCESS_STATUSES.includes(invoice.transaction_status)
                    const showDebtCollectionBadge = invoice.debt_collection && !isPaidStatus
                    const hadDebtCollection = invoice.debt_collection && isPaidStatus
                    const statusKey = (() => {
                        if (showDebtCollectionBadge) {
                            return 'debt_collection'
                        }
                        if (invoice.marked_lost) {
                            return 'marked_lost'
                        }
                        return invoice.transaction_status
                    })()
                    const statusText = (() => {
                        if (showDebtCollectionBadge) {
                            return (
                                <>
                                    <s>{invoice.transaction_status}</s>
                                    <br /> Debt Coll.
                                </>
                            )
                        }
                        if (invoice.marked_lost) {
                            return <b>Lost</b>
                        }
                        return invoice.transaction_status
                    })()
                    return (
                        <span title={invoice.transaction_last_updated}>
                            <Label {...statusMap[statusKey]} text={statusText} />
                            {hadDebtCollection && (
                                <>
                                    {' '}
                                    <Icon
                                        name="gavel"
                                        title={`Was in debt collection (${format(new Date(invoice.debt_collection), 'd MMM yyyy')}) — case paid`}
                                    />
                                </>
                            )}
                            {(invoice.invoices_logs?.length > 0 ||
                                invoice.mollie_logs?.length > 0) && (
                                <>
                                    {' '}
                                    <InvoiceStatusHistory invoice={invoice} />
                                </>
                            )}
                        </span>
                    )
                }
                case 'downloadInvoice':
                    return (
                        <A
                            target="_blank"
                            external
                            href={`${protocol}//${hostname}/api/office/invoices/download-invoice/${invoice.invoice_id}`}>
                            <Icon name="picture_as_pdf" />
                        </A>
                    )
                case 'mollie_payment_link':
                    return <MolliePaymentLink invoice={invoice} />
                case 'invoice_sent_date':
                case 'reminder_sent_date':
                case 'second_reminder_sent_date':
                case 'final_reminder_sent_date':
                    return invoice[key] && format(new Date(invoice[key]), 'd MMM')
                case 'pay_method':
                    return invoice[key] === 1 ? 'DD' : 'M'
                case 'canBeCharged':
                    return invoice[key] ? <Icon green name="card_membership" /> : ''
                case 'sendWhatsapp': {
                    if (!invoice.is_eligible_to_receive_whatsapp_reminder) {
                        return <></>
                    }
                    const whatsappTitle = (() => {
                        if (!invoice.is_valid_phone_number) {
                            return 'phone number is not valid'
                        }
                        if (!invoice.phone_number_is_in_white_list) {
                            return 'phone number is not in white list, on test it should belong to Niels, Rick, Freek or Dion'
                        }
                        if (invoice.invoice_overdue_manual_trigger_already_sent) {
                            return 'message has already been sent for this invoice'
                        }
                        return 'send a payment reminder message to the provider containing a link to his invoice page'
                    })()
                    return (
                        <Button
                            disabled={
                                !invoice.is_valid_phone_number ||
                                !invoice.phone_number_is_in_white_list
                            }
                            title={whatsappTitle}
                            onClick={() => {
                                NiceModal.show(ConfirmSendWhatsAppMessageModal, {
                                    invoiceId: invoice.invoice_id,
                                })
                            }}
                            appearance={
                                invoice.invoice_overdue_manual_trigger_already_sent
                                    ? 'whatsapp_outlined'
                                    : 'whatsapp'
                            }
                            iconBefore={<WhatsappSvg />}
                        />
                    )
                }
                case 'businessName':
                    return (
                        <Link
                            title={invoice[key]}
                            to={`/service/providers/provider/${invoice.service_provider_id}/company`}>
                            <Ellipsis style={{ maxWidth: 240 }}>{invoice[key]}</Ellipsis>
                        </Link>
                    )
                default:
                    return invoice[key]
            }
        }),
        expandable: (
            <Card expandableStyle>
                <InvoiceItem invoiceId={id} />
            </Card>
        ),
        isLoading: invoice._isLoading,
        backgroundColor: rowBackgroundColor,
    }
}

const InvoicesList = ({ filterId = 'all', defaultOptions = {}, showIncassoLink }) => {
    const { countries } = useAppSelector((state) => state.countries)
    const { protocol, hostname } = window.location
    const { query: urlQuery, onSetQuery } = useUrlQuery({
        aboutMonth: format(sub(new Date(), { months: 1 }), 'Y-MM'),
        ...defaultOptions,
    })
    const {
        filter = {},
        entities,
        currentPage,
        selectable = true,
        pageNr,
        ...rest
    } = useInvoicesList(filterId, { ...urlQuery, countries })
    const { invoices } = entities

    const changeFilter = (changes) => {
        onSetQuery({ ...changes, selection: [] })
    }
    const { minAmount, setMinAmount, maxAmount, setMaxAmount } = useDebouncedAmountFilter(
        urlQuery,
        filter,
        changeFilter,
    )
    const rows = {}
    const [openModal, setOpenModal] = useState(false)
    const [expanded, setExpanded] = useState(false)
    const [query, setQuery] = useState('')
    const [showCustomDate, setShowCustomDate] = useState(false)
    const [isSepaLoading, setIsSepaLoading] = useState(false)
    const [showSepaProgress, setShowSepaProgress] = useState(false)
    const [sepaErrorMessage, setSepaErrorMessage] = useState('')
    const [partialPaymentAmount, setPartialPaymentAmount] = useState('')
    const sepaProgressTimeoutRef = useRef(null)
    const paytCostsRequestRef = useRef(0)
    const [paytCosts, setPaytCosts] = useState(null)
    const [troyCases, setTroyCases] = useState([])
    const [isPaytCostsLoading, setIsPaytCostsLoading] = useState(false)
    const {
        createTransactions,
        setTransactionFulfilled,
        setTransactionUnfulfilled,
        fetchPaytFulfilmentCosts,
        cancelTransactions,
        disableOverdueInvoice,
        deleteInvoices,
        addToDebtCollection,
        createWIKLetterLink,
        isLoading,
        response,
        clearResponse,
        createSecondPayment,
        sendToNotify,
        markLost,
        addPartialPayment,
    } = useInvoiceActions(filterId)

    currentPage.forEach((id) => {
        rows[id] = renderRow(id, entities, expanded)
    })
    const selection = filter.selection || []
    const hasSelection = selection.length > 0
    const hasCancelableSelection = selection.every((id) =>
        ['scheduled', 'rescheduled', 'pending', 'queued'].includes(invoices[id].transaction_status),
    )
    const hasFulfilledSelection = selection.every(
        (id) => invoices[id].transaction_status === 'fulfilled',
    )
    const hasDeletableSelection = selection.every((id) => invoices[id].is_deletable)
    const hasSingleSelection = selection.length === 1
    const selectedInvoice = hasSingleSelection
        ? (invoices[selection[0]] ?? invoices[Number(selection[0])] ?? null)
        : null
    const isSelectedInvoiceOpen =
        selectedInvoice &&
        !SUCCESS_STATUSES.includes(selectedInvoice.transaction_status) &&
        selectedInvoice.transaction_status !== 'payment_plan'
    const isSelectedEligibleForPartialPayment =
        hasSingleSelection &&
        selectedInvoice &&
        (isSelectedInvoiceOpen || selectedInvoice.transaction_status === 'payment_plan')
    const selectedInvoiceOpenAmount = selectedInvoice ? getInvoiceAmounts(selectedInvoice).open : 0
    const notInPaytBlock =
        isSelectedEligibleForPartialPayment && !selectedInvoice?.can_add_partial_payment
    // Backend payt_sync_status: 'not_in_payt' | 'synced' | 'unavailable'. Without the distinction
    // a Payt outage looked like "invoice not in Payt" and sent people looking in the wrong place.
    const partialPaymentBlockedReason =
        selectedInvoice?.payt_sync_status === 'unavailable'
            ? 'Payt is currently unavailable, so the open amount could not be loaded. Reload the list to try again.'
            : 'Invoice not in Payt. Partial payment can only be registered for invoices sent to Payt.'
    const parsedResponse = response ? JSON.parse(response) : null
    const formattedResponse = response && <pre>{JSON.stringify(parsedResponse, null, 2)}</pre>
    // set-transaction-(un)fulfilled report per-invoice Payt sync failures; those invoices were
    // reverted server-side, so they must stand out from the JSON dump of the successes.
    const responseErrors = Array.isArray(parsedResponse?.errors) ? parsedResponse.errors : []
    const formattedResponseErrors = responseErrors.length > 0 && (
        <div style={{ color: '#d32f2f' }}>
            <b>
                {responseErrors.length} invoice{responseErrors.length === 1 ? ' was' : 's were'} not
                changed because Payt could not be updated:
            </b>
            <ul>
                {responseErrors.map((error) => (
                    <li key={error.invoice_id}>{error.message}</li>
                ))}
            </ul>
        </div>
    )

    useEffect(() => setShowCustomDate(false), [selection])
    useEffect(() => {
        rest.changeFilter({ ...urlQuery, selection: [] })
        if (urlQuery.query) {
            setQuery(urlQuery.query)
        }
    }, [urlQuery])
    useEffect(() => {
        return () => {
            if (sepaProgressTimeoutRef.current) {
                clearTimeout(sepaProgressTimeoutRef.current)
            }
        }
    }, [])
    const closeModal = () => {
        setOpenModal(false)
        clearResponse()
        setSepaErrorMessage('')
    }
    // Payt closes the credit case and waives all debt collection costs when an invoice in debt
    // collection is marked paid, so the costs are fetched upfront and CS decides explicitly
    // whether to waive them (TRUS-8184).
    const openSetFulfilledModal = () => {
        setOpenModal('setFullfilled')
        clearResponse()
        setPaytCosts(null)
        setTroyCases([])
        setIsPaytCostsLoading(true)
        // The request id keeps a response from an earlier open (stale selection) from
        // overwriting the check that belongs to this open.
        const requestId = ++paytCostsRequestRef.current
        fetchPaytFulfilmentCosts()
            .then((res) => {
                if (requestId === paytCostsRequestRef.current) {
                    setPaytCosts(res.invoices || [])
                    setTroyCases(res.troy || [])
                }
            })
            .catch(() => {
                if (requestId === paytCostsRequestRef.current) {
                    setPaytCosts(false)
                }
            })
            .finally(() => {
                if (requestId === paytCostsRequestRef.current) {
                    setIsPaytCostsLoading(false)
                }
            })
    }
    const paytCaseInvoices = Array.isArray(paytCosts) ? paytCosts : []
    const paytCasesWithCosts = paytCaseInvoices.filter((item) => !item.check_failed)
    const paytChecksFailed = paytCaseInvoices.filter((item) => item.check_failed)
    // A live case receives the payment automatically on fulfil; a stopped one cannot.
    const troyLiveCases = troyCases.filter((item) => !item.stopped)
    const troyStoppedCases = troyCases.filter((item) => item.stopped)
    // A failed check must not silently allow waiving, so it also requires the explicit choice.
    const showPaytDecision =
        paytCosts === false || paytCasesWithCosts.length > 0 || paytChecksFailed.length > 0
    const paytTotalCosts = paytCasesWithCosts.reduce(
        (sum, item) => sum + Number(item.debt_collection_costs) + Number(item.interest_costs),
        0,
    )
    const downloadCreditInvoicesSepa = async () => {
        if (!hasSelection || isSepaLoading) {
            return
        }
        setIsSepaLoading(true)
        setShowSepaProgress(false)
        if (sepaProgressTimeoutRef.current) {
            clearTimeout(sepaProgressTimeoutRef.current)
        }
        sepaProgressTimeoutRef.current = setTimeout(() => {
            setShowSepaProgress(true)
        }, 5000)
        try {
            const response = await call(
                `office/invoices/download-credit-invoices-sepa/${countries[0]}`,
                { json: { invoiceIds: selection } },
            )
            const blob = new Blob([response.xml], { type: 'application/xml' })
            const downloadUrl = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = downloadUrl
            link.download = response.filename || 'credit-invoices-sepa.xml'
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(downloadUrl)

            rest.changeFilter({ selection: [] })
        } catch (error) {
            const message =
                error instanceof Error && error.message ? error.message : 'SEPA download failed'
            setSepaErrorMessage(message)
            setOpenModal('sepaError')
        } finally {
            if (sepaProgressTimeoutRef.current) {
                clearTimeout(sepaProgressTimeoutRef.current)
            }
            setIsSepaLoading(false)
            setShowSepaProgress(false)
        }
    }
    let sepaButtonContent = <PreIcon icon="soap">Credit invoices SEPA</PreIcon>
    if (isSepaLoading) {
        sepaButtonContent = showSepaProgress ? (
            <span>
                Generating SEPA file <LoadingDots color="#fff" />
            </span>
        ) : (
            <span>Preparing SEPA file</span>
        )
    }

    // Build the export URL with the currently active filters, so the export mirrors the on-screen list.
    const exportFilter = {}
    Object.keys(filter).forEach((key) => {
        if (key === 'selection' || key === 'page' || key === 'pageSize' || key === 'searchKey') {
            return
        }
        const value = filter[key]
        if (value === undefined || value === null || value === '') {
            return
        }
        if (Array.isArray(value)) {
            exportFilter[key + '[]'] = value
        } else {
            exportFilter[key] = value
        }
    })
    const buildExportHref = (variant) =>
        `${protocol}//${hostname}/api/office/invoices/export-list?${queryString.stringify({
            ...exportFilter,
            variant,
        })}`

    return (
        <LoadBoxWrapper isLoading={rest.isFetching}>
            <Divider />
            <AutoRow>
                <MonthPicker
                    value={filter.aboutMonth}
                    onChange={(aboutMonth) => changeFilter({ aboutMonth: aboutMonth ?? '' })}
                    startYearMonth="2019-10"
                />
                <SelectSimple
                    value={filter.status}
                    placeholder="All invoices"
                    alwaysControlValue
                    options={[
                        { name: 'all', value: 'all' },
                        { name: 'Open (all unpaid)', value: 'openUnpaid' },
                        { name: 'Manual All', value: 'manualAll' },
                        { name: 'Manual Open', value: 'manualOpen' },
                        { name: 'Invoice mail not sent', value: 'invoiceMailNotSent' },
                        { name: 'Manual Paid', value: 'manualPaid' },
                        {
                            name: 'Credit limit invoices (interim invoices)',
                            value: 'creditLimitInterim',
                        },
                        { name: 'failed Reason Storno (MD06)', value: 'failedReasonStorno' },
                        { name: 'failed Reason Storno (AM04)', value: 'failedReasonInsufficient' },
                        { name: 'failed Reason Other', value: 'failedReasonOther' },
                        { name: 'Sent to Notify', value: 'sentToNotify' },
                        { name: 'Not sent to Notify', value: 'notSentToNotify' },
                        { name: 'Sent to Troy', value: 'sentToTroy' },
                        { name: 'Not sent to Troy', value: 'notSentToTroy' },
                        { name: 'Mandate Active', value: 'mandateActive' },
                        {
                            name: 'Mandate Active No Transaction',
                            value: 'mandateActiveNoTransaction',
                        },
                        {
                            name: 'Subscribed But Mandate Cancelled',
                            value: 'subscribedButMandateCancelled',
                        },
                        { name: 'Not Mandate Active', value: 'notMandateActive' },
                        { name: 'No Mandate', value: 'noMandate' },
                        { name: 'Debt collection', value: 'debt_collection' },
                        { name: 'Lost', value: 'marked_lost' },
                        { name: 'Eligible for payout', value: 'payout' },
                        { name: 'No status', value: 'noStatus' },
                        ...Object.keys(statusMap).map((status) => {
                            return {
                                name: status,
                                value: status,
                            }
                        }),
                    ]}
                    onChange={(e) => changeFilter({ status: e.target.value })}
                />
                {Boolean(filter.debtorBucket) && (
                    <button
                        type="button"
                        title="Filtered on a status bucket from the Debtors page. Click to remove this filter."
                        onClick={() => changeFilter({ debtorBucket: '' })}
                        className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-3 py-1 text-sm font-medium text-primary-700 hover:bg-primary-100">
                        {STATUS_META[filter.debtorBucket]?.label || filter.debtorBucket}
                        <Icon name="close" fontSize={14} />
                    </button>
                )}
                <CheckButton
                    msg="expanded"
                    checked={expanded}
                    onChange={() => setExpanded(!expanded)}
                />
                <CheckButton
                    msg="open"
                    checked={parseInt(filter.open)}
                    onChange={() => changeFilter({ open: filter.open ? 0 : 1 })}
                />
                <CheckButton
                    msg="Has unpaid invoice followup"
                    checked={filter.hasUnpaidInvoiceFollowUp}
                    onChange={() =>
                        changeFilter({
                            hasUnpaidInvoiceFollowUp: filter.hasUnpaidInvoiceFollowUp === 1 ? 0 : 1,
                        })
                    }
                />
                <CheckButton
                    msg="Not paid"
                    checked={parseInt(filter.notPaid)}
                    onChange={() =>
                        changeFilter({
                            notPaid: filter.notPaid ? 0 : 1,
                        })
                    }
                />
                {Boolean(rest.count) && (
                    <Pagination
                        current={pageNr}
                        pageSize={parseInt(filter.pageSize)}
                        count={parseInt(rest.count)}
                        onPageChange={(page) => changeFilter({ page })}
                    />
                )}
                <a target="_blank" rel="noopener noreferrer" href={buildExportHref('basic')}>
                    <PreIcon name="exit_to_app">Export basic</PreIcon>
                </a>
                <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={buildExportHref('comprehensive')}>
                    <PreIcon name="exit_to_app">Export full</PreIcon>
                </a>
                <InputField
                    icon="search"
                    placeholder="Enter query and press enter"
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value)
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            changeFilter({ query })
                        }
                    }}
                />
                <SelectSimple
                    options={[
                        { value: '<0', name: '< 0' },
                        { value: '>0', name: '> 0' },
                        { value: '<100', name: '< 100' },
                        { value: '100 - 500', name: '100 - 500' },
                        { value: '500 - 1000', name: '500 - 1000' },
                        { value: '>1000', name: '> 1000' },
                    ]}
                    onChange={(e) => {
                        setMinAmount('')
                        setMaxAmount('')
                        changeFilter({
                            invoiceAmount: e.target.value,
                            minInvoiceAmount: '',
                            maxInvoiceAmount: '',
                        })
                    }}
                    value={filter.invoiceAmount}
                    emptyOption
                    alwaysControlValue
                    placeholder="min invoice amount"
                />
                <InputField
                    type="number"
                    step="0.01"
                    placeholder="min €netto"
                    value={minAmount}
                    onChange={(e) => setMinAmount(e.target.value)}
                />
                <InputField
                    type="number"
                    step="0.01"
                    placeholder="max €netto"
                    value={maxAmount}
                    onChange={(e) => setMaxAmount(e.target.value)}
                />
                <ServicesSelect
                    onChangeValue={(serviceIds) => changeFilter({ serviceIds })}
                    value={(() => {
                        // Deep links / page reloads put serviceIds in the URL as '12' or '12,34'.
                        if (Array.isArray(filter.serviceIds)) {
                            return filter.serviceIds
                        }
                        if (filter.serviceIds) {
                            return String(filter.serviceIds)
                                .split(',')
                                .map((id) => parseInt(id.trim(), 10))
                                .filter((id) => Number.isInteger(id) && id > 0)
                        }
                        return []
                    })()}
                    placeholder="Select service"
                    emptyOption
                />
                <SelectSimple
                    options={[
                        { value: 20, name: '20' },
                        { value: 100, name: '100' },
                        { value: 200, name: '200' },
                        { value: 250, name: '250' },
                        { value: 300, name: '300' },
                        { value: 999, name: '999' },
                        { value: 9999, name: '9999' },
                    ]}
                    onChange={(e) => changeFilter({ pageSize: e.target.value })}
                    value={filter.pageSize || 20}
                    placeholder="nr results"
                />
                <button onClick={() => changeFilter({ didInvalidate: true })}>
                    <Icon name="refresh" />
                </button>
                <BatchMailButton
                    selection={hasSelection ? filter.selection : []}
                    filterId={filterId}
                />
                {hasSelection && showIncassoLink && (
                    <a rel="noreferrer" target="_blank" href={createWIKLetterLink()}>
                        <PreIcon icon="request_page">WIK</PreIcon>
                    </a>
                )}
                {hasSelection && (
                    <Button name="default" onClick={() => setOpenModal('transactions')}>
                        <PreIcon icon="payment">Create transactions</PreIcon>
                    </Button>
                )}
                <Modal
                    open={openModal === 'transactions'}
                    backgroundColor="transparent"
                    allowOverflow
                    close={closeModal}>
                    <h3>Create transaction (Mollie) for {selection.length} invoices</h3>
                    <Divider />
                    {formattedResponse}
                    <Divider />
                    <Button
                        name="borderedPoppins"
                        disabled={isLoading}
                        onClick={createTransactions}>
                        {isLoading ? <LoadingDots color="#fff" /> : 'Create transactions'}
                    </Button>
                </Modal>
                {hasSelection && (
                    <Button name="discard" onClick={() => setOpenModal('secondPayments')}>
                        <PreIcon red icon="gpp_maybe">
                            Create second payments
                        </PreIcon>
                    </Button>
                )}
                <Modal
                    open={openModal === 'secondPayments'}
                    backgroundColor="transparent"
                    allowOverflow
                    close={closeModal}>
                    <h3>
                        Create second payment for invoice because last payment failed (
                        {selection.length} invoices)
                    </h3>
                    <Divider />
                    {formattedResponse}
                    <Divider />
                    <Button name="discard" disabled={isLoading} onClick={createSecondPayment}>
                        {isLoading ? <LoadingDots color="#fff" /> : 'Create second payments'}
                    </Button>
                </Modal>

                {hasSelection && (
                    <Button name="borderedPoppins" onClick={() => setOpenModal('send2notify')}>
                        <PreIcon icon="monetization_on">Send to Notify</PreIcon>
                    </Button>
                )}
                <Modal
                    open={openModal === 'send2notify'}
                    backgroundColor="transparent"
                    allowOverflow
                    close={closeModal}>
                    <h3>Send to Notify: {selection.length} invoices</h3>
                    <Divider />
                    {formattedResponse}
                    <Divider />
                    <Button name="borderedPoppins" disabled={isLoading} onClick={sendToNotify}>
                        {isLoading ? <LoadingDots color="#fff" /> : <>Send to Notfiy</>}
                    </Button>
                </Modal>

                {hasSelection && (
                    <Button name="borderedPoppins" onClick={openSetFulfilledModal}>
                        <PreIcon icon="payment">Set transactions as fulfilled</PreIcon>
                    </Button>
                )}
                <Modal open={openModal === 'setFullfilled'} close={closeModal}>
                    <h3>Set transaction to fulfilled for {selection.length} invoices</h3>
                    <Divider />
                    {formattedResponseErrors}
                    {formattedResponse}
                    <Divider />
                    {isPaytCostsLoading && (
                        <div>
                            Checking Payt debt collection costs <LoadingDots />
                        </div>
                    )}
                    {!isPaytCostsLoading && paytCosts === false && (
                        <p style={{ color: '#ff8000' }}>
                            Warning: could not check the Payt debt collection costs and Troy cases
                            for this selection. Waiving closes any active Payt credit case and
                            waives its costs and interest.
                        </p>
                    )}
                    {!isPaytCostsLoading && troyLiveCases.length > 0 && (
                        <p style={{ color: '#10185a' }}>
                            {troyLiveCases.length === 1
                                ? 'A selected invoice is'
                                : `${troyLiveCases.length} selected invoices are`}{' '}
                            at Troy:{' '}
                            {troyLiveCases
                                .map((item) => `${item.invoice_nr} (case ${item.troy_case_id})`)
                                .join(', ')}
                            . The payment is registered on the Troy case automatically when you
                            fulfil, so Troy stops collecting that amount. Check the provider notes
                            afterwards: if the registration failed, a note explains what to do.
                        </p>
                    )}
                    {!isPaytCostsLoading && troyStoppedCases.length > 0 && (
                        <p style={{ color: '#ff002f' }}>
                            {troyStoppedCases.length === 1
                                ? 'A selected invoice is'
                                : `${troyStoppedCases.length} selected invoices are`}{' '}
                            on a stopped Troy case:{' '}
                            {troyStoppedCases
                                .map((item) => `${item.invoice_nr} (case ${item.troy_case_id})`)
                                .join(', ')}
                            . A stopped case cannot receive the payment automatically. Resume the
                            case via &quot;Manage Troy case&quot; on the provider page first, or
                            register the payment with Troy by hand.
                        </p>
                    )}
                    {!isPaytCostsLoading && paytChecksFailed.length > 0 && (
                        <p style={{ color: '#ff8000' }}>
                            Warning: could not check the Payt credit case for:{' '}
                            {paytChecksFailed.map((item) => item.invoice_nr).join(', ')}
                        </p>
                    )}
                    {!isPaytCostsLoading && paytCasesWithCosts.length > 0 && (
                        <>
                            <p style={{ color: '#ff002f' }}>
                                {paytCasesWithCosts.length} selected invoice
                                {paytCasesWithCosts.length === 1 ? ' has' : 's have'} an active Payt
                                credit case. Waiving closes the case and waives these debt
                                collection costs:
                            </p>
                            <table style={{ borderSpacing: '16px 2px' }}>
                                <thead>
                                    <tr>
                                        <th align="left">invoice nr</th>
                                        <th align="left">Payt case</th>
                                        <th align="right">collection costs</th>
                                        <th align="right">interest</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paytCasesWithCosts.map((item) => (
                                        <tr key={item.invoice_id}>
                                            <td>{item.invoice_nr}</td>
                                            <td>{item.case_number}</td>
                                            <td align="right">
                                                €{Number(item.debt_collection_costs).toFixed(2)}
                                            </td>
                                            <td align="right">
                                                €{Number(item.interest_costs).toFixed(2)}
                                            </td>
                                        </tr>
                                    ))}
                                    <tr>
                                        <td>
                                            <b>total</b>
                                        </td>
                                        <td />
                                        <td align="right" colSpan={2}>
                                            <b>€{paytTotalCosts.toFixed(2)}</b>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <Divider />
                        </>
                    )}
                    {!isPaytCostsLoading && !showPaytDecision && (
                        <Button onClick={() => setTransactionFulfilled(true)}>
                            {isLoading ? (
                                <LoadingDots color="#fff" />
                            ) : (
                                'Set transactions to fulfilled'
                            )}
                        </Button>
                    )}
                    {/* The two actions are contradictory, so they lock while a request runs
                        and disappear once one has succeeded (response is set). */}
                    {!isPaytCostsLoading && showPaytDecision && !response && (
                        <AutoRow>
                            <Button
                                name="discard"
                                onClick={() => !isLoading && setTransactionFulfilled(true)}>
                                {isLoading ? (
                                    <LoadingDots color="#fff" />
                                ) : (
                                    'Fulfil & waive Payt costs (closes case)'
                                )}
                            </Button>
                            <Button onClick={() => !isLoading && setTransactionFulfilled(false)}>
                                {isLoading ? (
                                    <LoadingDots color="#fff" />
                                ) : (
                                    'Fulfil only (keep Payt case open)'
                                )}
                            </Button>
                        </AutoRow>
                    )}
                </Modal>

                {hasSelection && hasFulfilledSelection && (
                    <Button name="borderedPoppins" onClick={() => setOpenModal('setUnfulfilled')}>
                        <PreIcon icon="undo">Set transactions as unfulfilled</PreIcon>
                    </Button>
                )}
                <Modal open={openModal === 'setUnfulfilled'} close={closeModal}>
                    <h3>Set transaction to unfulfilled for {selection.length} invoices</h3>
                    <Divider />
                    {formattedResponseErrors}
                    {formattedResponse}
                    <Divider />
                    <Button onClick={setTransactionUnfulfilled}>
                        {isLoading ? (
                            <LoadingDots color="#fff" />
                        ) : (
                            'Set transactions to unfulfilled'
                        )}
                    </Button>
                </Modal>

                {isSelectedEligibleForPartialPayment &&
                    (notInPaytBlock ? (
                        <span title={partialPaymentBlockedReason}>
                            <Button name="borderedPoppins" disabled>
                                <PreIcon icon="payment">Add Partial Payment</PreIcon>
                            </Button>
                        </span>
                    ) : (
                        <Button
                            name="borderedPoppins"
                            onClick={() => {
                                setPartialPaymentAmount('')
                                setOpenModal('addPartialPayment')
                            }}>
                            <PreIcon icon="payment">Add Partial Payment</PreIcon>
                        </Button>
                    ))}
                <Modal
                    open={openModal === 'addPartialPayment'}
                    close={() => {
                        setOpenModal(false)
                        setPartialPaymentAmount('')
                        clearResponse()
                    }}>
                    <h3>Add partial payment</h3>
                    <Divider />
                    <InputField
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Amount (€)"
                        value={partialPaymentAmount}
                        onChange={(e) => setPartialPaymentAmount(e.target.value)}
                    />
                    {partialPaymentAmount && !Number.isNaN(Number(partialPaymentAmount)) && (
                        <p style={{ marginTop: 8 }}>
                            This will reduce the open amount by €
                            {Number(partialPaymentAmount).toFixed(2)} in Payt.
                        </p>
                    )}
                    {response && <pre>{response}</pre>}
                    <Divider />
                    <Button
                        disabled={
                            isLoading ||
                            !partialPaymentAmount ||
                            Number(partialPaymentAmount) <= 0 ||
                            Number(partialPaymentAmount) > selectedInvoiceOpenAmount
                        }
                        onClick={() =>
                            addPartialPayment(partialPaymentAmount).then(() => {
                                setOpenModal(false)
                                setPartialPaymentAmount('')
                                clearResponse()
                            })
                        }>
                        {isLoading ? <LoadingDots color="#fff" /> : 'Confirm partial payment'}
                    </Button>
                </Modal>

                {hasSelection && hasCancelableSelection && (
                    <Button name="delete" onClick={() => setOpenModal('cancelTransactions')}>
                        Cancel transactions
                    </Button>
                )}
                <Modal open={openModal === 'cancelTransactions'} close={closeModal}>
                    <h3>Cancel transaction (Mollie) for {selection.length} invoices</h3>
                    <Divider />
                    {formattedResponse}
                    <Divider />
                    <Button name="borderedPoppins" onClick={cancelTransactions}>
                        {isLoading ? <LoadingDots color="#fff" /> : 'Create transactions'}
                    </Button>
                </Modal>
                {hasSelection && (
                    <Button name="delete" onClick={() => setOpenModal('disableOverdueInvoice')}>
                        Disable (overdue invoice)
                    </Button>
                )}
                <Modal open={openModal === 'disableOverdueInvoice'} close={closeModal}>
                    <h3>disableOverdueInvoice {selection.length} invoices</h3>
                    <Divider />
                    {formattedResponse}
                    <Divider />
                    <AutoRow>
                        <Button name="borderedPoppins" onClick={() => disableOverdueInvoice(false)}>
                            {isLoading ? <LoadingDots color="#fff" /> : 'disableOverdueInvoice'}
                        </Button>
                        <Button name="borderedPoppins" onClick={() => disableOverdueInvoice(true)}>
                            {isLoading ? (
                                <LoadingDots color="#fff" />
                            ) : (
                                'unset disableOverdueInvoice'
                            )}
                        </Button>
                    </AutoRow>
                </Modal>
                {hasSelection && (
                    <Button name="borderedPoppins" onClick={() => setOpenModal('debtCollection')}>
                        <PreIcon icon="local_atm">Add to debt collection</PreIcon>
                    </Button>
                )}
                {hasSelection && (
                    <Button name="borderedPoppins" onClick={() => setOpenModal('markLost')}>
                        <PreIcon icon="heart_broken">Mark lost</PreIcon>
                    </Button>
                )}
                <Modal open={openModal === 'debtCollection'} close={closeModal}>
                    <h3>Add {selection.length} invoices to debt collection</h3>
                    <Divider m />
                    <p>
                        This only marks the selected invoices as in debt collection here in Office:
                        they get the debt collection badge in the list and an entry in the invoice
                        status log. Nothing is sent to Troy, Payt or the debtor.
                    </p>
                    <p style={{ marginTop: 8 }}>
                        To actually hand a debt over for collection, use &quot;Manage Troy
                        case&quot;. Invoices sent to Troy (or escalated by Payt) get this mark
                        automatically. &quot;Remove from debt collection&quot; clears the mark
                        again; it does not stop a real collection case.
                    </p>
                    {formattedResponse}
                    <Divider m />
                    <AutoRow>
                        <Button
                            onClick={() => addToDebtCollection().then(closeModal)}
                            disabled={isLoading}>
                            {isLoading ? (
                                <LoadingDots color="#fff" />
                            ) : (
                                <span>Add to debt collection ({selection.length} invoices)</span>
                            )}
                        </Button>
                        <Button
                            onClick={() => addToDebtCollection(true).then(closeModal)}
                            name="borderedPoppins"
                            disabled={isLoading}>
                            {isLoading ? (
                                <LoadingDots color="#fff" />
                            ) : (
                                <span>
                                    Remove from debt collection ({selection.length} invoices)
                                </span>
                            )}
                        </Button>
                    </AutoRow>
                </Modal>
                <Modal open={openModal === 'markLost'} close={closeModal}>
                    <h3>Mark {selection.length} invoices as lost</h3>
                    <Divider m />
                    {formattedResponse}
                    <Divider m />
                    <AutoRow>
                        <Button onClick={() => markLost().then(closeModal)} disabled={isLoading}>
                            {isLoading ? (
                                <LoadingDots color="#fff" />
                            ) : (
                                <span>Mark lost ({selection.length})</span>
                            )}
                        </Button>
                        <Button
                            onClick={() => markLost(true).then(closeModal)}
                            name="borderedPoppins"
                            disabled={isLoading}>
                            {isLoading ? (
                                <LoadingDots color="#fff" />
                            ) : (
                                <span>Undo mark lost ({selection.length})</span>
                            )}
                        </Button>
                    </AutoRow>
                </Modal>
                {hasSelection && (
                    <Button
                        name="borderedPoppins"
                        onClick={downloadCreditInvoicesSepa}
                        disabled={isSepaLoading}>
                        {sepaButtonContent}
                    </Button>
                )}
                <Modal open={openModal === 'sepaError'} close={closeModal}>
                    <h3>SEPA download failed</h3>
                    <Divider m />
                    <pre>{sepaErrorMessage}</pre>
                    <Divider m />
                    <Button onClick={closeModal}>Close</Button>
                </Modal>
                {hasSelection && hasDeletableSelection && (
                    <Button name="discard" onClick={() => setOpenModal('deleteInvoice')}>
                        <PreIcon red icon="delete">
                            Delete invoice
                        </PreIcon>
                    </Button>
                )}
                <Modal open={openModal === 'deleteInvoice'} close={closeModal}>
                    <h3>Delete {selection.length} invoices</h3>
                    <Divider />
                    {formattedResponse}
                    <Divider />
                    <Button onClick={() => deleteInvoices().then(closeModal)}>
                        {isLoading ? <LoadingDots color="#fff" /> : 'delete invoice'}
                    </Button>
                </Modal>
            </AutoRow>
            <Divider m />
            <TableList
                {...rest}
                selectable={selectable}
                changeFilter={rest.changeFilter || (() => {})}
                filter={filter}
                rows={rows}
                definitions={expanded ? definitions : definitionsBrief}
            />
        </LoadBoxWrapper>
    )
}

export default InvoicesList
